import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

/**
 * Split button — a primary action plus an adjacent dropdown of secondary
 * actions (shadcn/Radix). Matches the "[Action] [▾]" pattern.
 *
 * Props:
 *   children      — primary button label/content
 *   onClick       — primary action handler
 *   items         — [{ label, icon?, onClick, disabled? }]
 *   variant       — "primary" (default) | "secondary"
 *   icon          — optional leading icon for the primary button
 *   align         — dropdown alignment ("end" default)
 */
function SplitButton({
  children,
  onClick,
  items = [],
  variant = "primary",
  icon: Icon,
  align = "end",
  className,
}) {
  const base =
    "inline-flex h-9 items-center gap-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";
  const styles =
    variant === "secondary"
      ? "bg-background text-foreground border border-input hover:bg-muted"
      : "bg-primary text-primary-foreground hover:bg-primary/90";

  return (
    <div className={cn("inline-flex", className)}>
      <button
        type="button"
        onClick={onClick}
        className={cn(base, styles, "rounded-l-lg px-3")}
      >
        {Icon && <Icon className="size-4" />}
        {children}
      </button>
      <div
        className={cn(
          "w-px self-stretch",
          variant === "secondary" ? "bg-border" : "bg-primary-foreground/30",
        )}
      />
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(base, styles, "rounded-r-lg px-2")}
          aria-label="More actions"
        >
          <ChevronDown className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="min-w-52">
          {items.map((item, i) => (
            <DropdownMenuItem
              key={i}
              onClick={item.onClick}
              disabled={item.disabled}
            >
              {item.icon && <item.icon />}
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export { SplitButton };
